// src/data/bingoPatterns.js

/*
  BUZZ BOARD - BINGO PATTERN LIBRARY

  Grid coordinates:
  row:    0 - 4
  column: 0 - 4

        B  I  N  G  O
      ---------------
  0 |   .  .  .  .  .
  1 |   .  .  .  .  .
  2 |   .  .  F  .  .
  3 |   .  .  .  .  .
  4 |   .  .  .  .  .

  Each mask is a 5x5 boolean grid.

  true  = required cell
  false = not required

  Patterns may use:

  type: 'fixed'
    One exact pattern mask.

  type: 'anyOf'
    Any one of several masks satisfies the pattern.

  Later we can add types such as:
    'lines'
    'count'
    'custom'
    'rotatable'
*/


// ============================================================
// MASK HELPERS
// ============================================================

export const createEmptyMask = () =>
  Array.from(
    { length: 5 },
    () => Array(5).fill(false)
  );


export const maskFromCells = (cells = []) => {
  const mask = createEmptyMask();

  cells.forEach(([row, column]) => {
    if (
      row >= 0 &&
      row < 5 &&
      column >= 0 &&
      column < 5
    ) {
      mask[row][column] = true;
    }
  });

  return mask;
};


export const cloneMask = (mask) =>
  mask.map((row) => [...row]);


export const rotateMask90 = (mask) => {
  const rotated = createEmptyMask();

  for (let row = 0; row < 5; row += 1) {
    for (
      let column = 0;
      column < 5;
      column += 1
    ) {
      rotated[column][4 - row] =
        Boolean(mask[row][column]);
    }
  }

  return rotated;
};


export const rotateMask180 = (mask) =>
  rotateMask90(
    rotateMask90(mask)
  );


export const rotateMask270 = (mask) =>
  rotateMask90(
    rotateMask180(mask)
  );


export const mirrorMaskHorizontal = (mask) =>
  mask.map((row) =>
    [...row].reverse()
  );


export const createRotations = (mask) => {
  const masks = [
    cloneMask(mask),
    rotateMask90(mask),
    rotateMask180(mask),
    rotateMask270(mask),
  ];

  /*
    Remove duplicate rotations.

    This matters for symmetrical patterns such
    as X, +, square, etc.
  */

  const unique = [];

  masks.forEach((candidate) => {
    const key =
      JSON.stringify(candidate);

    if (
      !unique.some(
        (existing) =>
          JSON.stringify(existing) === key
      )
    ) {
      unique.push(candidate);
    }
  });

  return unique;
};


// ============================================================
// BASIC MASK BUILDERS
// ============================================================

const horizontalRow = (row) =>
  maskFromCells(
    Array.from(
      { length: 5 },
      (_, column) => [
        row,
        column,
      ]
    )
  );


const verticalColumn = (column) =>
  maskFromCells(
    Array.from(
      { length: 5 },
      (_, row) => [
        row,
        column,
      ]
    )
  );


const MAIN_DIAGONAL =
  maskFromCells([
    [0, 0],
    [1, 1],
    [2, 2],
    [3, 3],
    [4, 4],
  ]);


const REVERSE_DIAGONAL =
  maskFromCells([
    [0, 4],
    [1, 3],
    [2, 2],
    [3, 1],
    [4, 0],
  ]);


// ============================================================
// REUSABLE MASKS
// ============================================================

const FOUR_CORNERS =
  maskFromCells([
    [0, 0],
    [0, 4],
    [4, 0],
    [4, 4],
  ]);


const FULL_CARD =
  Array.from(
    { length: 5 },
    () => Array(5).fill(true)
  );


const X_PATTERN =
  maskFromCells([
    [0, 0],
    [0, 4],

    [1, 1],
    [1, 3],

    [2, 2],

    [3, 1],
    [3, 3],

    [4, 0],
    [4, 4],
  ]);


const PLUS_PATTERN =
  maskFromCells([
    [0, 2],
    [1, 2],

    [2, 0],
    [2, 1],
    [2, 2],
    [2, 3],
    [2, 4],

    [3, 2],
    [4, 2],
  ]);


const T_PATTERN =
  maskFromCells([
    [0, 0],
    [0, 1],
    [0, 2],
    [0, 3],
    [0, 4],

    [1, 2],
    [2, 2],
    [3, 2],
    [4, 2],
  ]);


const L_PATTERN =
  maskFromCells([
    [0, 0],
    [1, 0],
    [2, 0],
    [3, 0],
    [4, 0],

    [4, 1],
    [4, 2],
    [4, 3],
    [4, 4],
  ]);


const H_PATTERN =
  maskFromCells([
    [0, 0],
    [1, 0],
    [2, 0],
    [3, 0],
    [4, 0],

    [2, 1],
    [2, 2],
    [2, 3],

    [0, 4],
    [1, 4],
    [2, 4],
    [3, 4],
    [4, 4],
  ]);


const U_PATTERN =
  maskFromCells([
    [0, 0],
    [1, 0],
    [2, 0],
    [3, 0],
    [4, 0],

    [4, 1],
    [4, 2],
    [4, 3],

    [0, 4],
    [1, 4],
    [2, 4],
    [3, 4],
    [4, 4],
  ]);


const V_PATTERN =
  maskFromCells([
    [0, 0],
    [1, 0],

    [2, 1],

    [3, 1],
    [4, 2],

    [3, 3],
    [2, 3],

    [1, 4],
    [0, 4],
  ]);


const Z_PATTERN =
  maskFromCells([
    [0, 0],
    [0, 1],
    [0, 2],
    [0, 3],
    [0, 4],

    [1, 3],
    [2, 2],
    [3, 1],

    [4, 0],
    [4, 1],
    [4, 2],
    [4, 3],
    [4, 4],
  ]);


const N_PATTERN =
  maskFromCells([
    [0, 0],
    [1, 0],
    [2, 0],
    [3, 0],
    [4, 0],

    [1, 1],
    [2, 2],
    [3, 3],

    [0, 4],
    [1, 4],
    [2, 4],
    [3, 4],
    [4, 4],
  ]);


const E_PATTERN =
  maskFromCells([
    [0, 0],
    [1, 0],
    [2, 0],
    [3, 0],
    [4, 0],

    [0, 1],
    [0, 2],
    [0, 3],
    [0, 4],

    [2, 1],
    [2, 2],
    [2, 3],

    [4, 1],
    [4, 2],
    [4, 3],
    [4, 4],
  ]);


const C_PATTERN =
  maskFromCells([
    [0, 0],
    [0, 1],
    [0, 2],
    [0, 3],
    [0, 4],

    [1, 0],
    [2, 0],
    [3, 0],

    [4, 0],
    [4, 1],
    [4, 2],
    [4, 3],
    [4, 4],
  ]);


const SMALL_DIAMOND =
  maskFromCells([
    [1, 2],

    [2, 1],
    [2, 3],

    [3, 2],
  ]);


const LARGE_DIAMOND =
  maskFromCells([
    [0, 2],

    [1, 1],
    [1, 3],

    [2, 0],
    [2, 4],

    [3, 1],
    [3, 3],

    [4, 2],
  ]);


const OUTSIDE_FRAME =
  maskFromCells([
    [0, 0],
    [0, 1],
    [0, 2],
    [0, 3],
    [0, 4],

    [1, 0],
    [1, 4],

    [2, 0],
    [2, 4],

    [3, 0],
    [3, 4],

    [4, 0],
    [4, 1],
    [4, 2],
    [4, 3],
    [4, 4],
  ]);


const INSIDE_FRAME =
  maskFromCells([
    [1, 1],
    [1, 2],
    [1, 3],

    [2, 1],
    [2, 3],

    [3, 1],
    [3, 2],
    [3, 3],
  ]);


const SMALL_SQUARE =
  maskFromCells([
    [1, 1],
    [1, 2],
    [1, 3],

    [2, 1],
    [2, 3],

    [3, 1],
    [3, 2],
    [3, 3],
  ]);


const BOW_TIE =
  maskFromCells([
    [0, 0],
    [0, 4],

    [1, 0],
    [1, 1],
    [1, 3],
    [1, 4],

    [2, 2],

    [3, 0],
    [3, 1],
    [3, 3],
    [3, 4],

    [4, 0],
    [4, 4],
  ]);


const HOURGLASS =
  maskFromCells([
    [0, 0],
    [0, 1],
    [0, 2],
    [0, 3],
    [0, 4],

    [1, 1],
    [1, 3],

    [2, 2],

    [3, 1],
    [3, 3],

    [4, 0],
    [4, 1],
    [4, 2],
    [4, 3],
    [4, 4],
  ]);


const CHRISTMAS_TREE =
  maskFromCells([
    [0, 2],

    [1, 1],
    [1, 2],
    [1, 3],

    [2, 0],
    [2, 1],
    [2, 2],
    [2, 3],
    [2, 4],

    [3, 1],
    [3, 2],
    [3, 3],

    [4, 2],
  ]);


const PYRAMID =
  maskFromCells([
    [0, 2],

    [1, 1],
    [1, 2],
    [1, 3],

    [2, 0],
    [2, 1],
    [2, 2],
    [2, 3],
    [2, 4],
  ]);


const ARROW_UP =
  maskFromCells([
    [0, 2],

    [1, 1],
    [1, 2],
    [1, 3],

    [2, 0],
    [2, 2],
    [2, 4],

    [3, 2],
    [4, 2],
  ]);


const KITE =
  maskFromCells([
    [0, 2],

    [1, 1],
    [1, 3],

    [2, 2],

    [3, 2],
    [4, 2],
  ]);


// ============================================================
// POSTAGE STAMPS
// ============================================================

const POSTAGE_STAMP_TOP_LEFT =
  maskFromCells([
    [0, 0],
    [0, 1],
    [1, 0],
    [1, 1],
  ]);


const POSTAGE_STAMP_TOP_RIGHT =
  maskFromCells([
    [0, 3],
    [0, 4],
    [1, 3],
    [1, 4],
  ]);


const POSTAGE_STAMP_BOTTOM_LEFT =
  maskFromCells([
    [3, 0],
    [3, 1],
    [4, 0],
    [4, 1],
  ]);


const POSTAGE_STAMP_BOTTOM_RIGHT =
  maskFromCells([
    [3, 3],
    [3, 4],
    [4, 3],
    [4, 4],
  ]);


// ============================================================
// SIX PACKS
// ============================================================

const createSixPackMasks = () => {
  const masks = [];

  /*
    2 rows x 3 columns
  */

  for (
    let row = 0;
    row <= 3;
    row += 1
  ) {
    for (
      let column = 0;
      column <= 2;
      column += 1
    ) {
      masks.push(
        maskFromCells([
          [row, column],
          [row, column + 1],
          [row, column + 2],

          [row + 1, column],
          [row + 1, column + 1],
          [row + 1, column + 2],
        ])
      );
    }
  }

  /*
    3 rows x 2 columns
  */

  for (
    let row = 0;
    row <= 2;
    row += 1
  ) {
    for (
      let column = 0;
      column <= 3;
      column += 1
    ) {
      masks.push(
        maskFromCells([
          [row, column],
          [row, column + 1],

          [row + 1, column],
          [row + 1, column + 1],

          [row + 2, column],
          [row + 2, column + 1],
        ])
      );
    }
  }

  return masks;
};


// ============================================================
// NINE PACKS
// ============================================================

const createNinePackMasks = () => {
  const masks = [];

  for (
    let row = 0;
    row <= 2;
    row += 1
  ) {
    for (
      let column = 0;
      column <= 2;
      column += 1
    ) {
      const cells = [];

      for (
        let rowOffset = 0;
        rowOffset < 3;
        rowOffset += 1
      ) {
        for (
          let columnOffset = 0;
          columnOffset < 3;
          columnOffset += 1
        ) {
          cells.push([
            row + rowOffset,
            column + columnOffset,
          ]);
        }
      }

      masks.push(
        maskFromCells(cells)
      );
    }
  }

  return masks;
};


// ============================================================
// BLOCK OF FOUR
// ============================================================

const createBlockOfFourMasks = () => {
  const masks = [];

  for (
    let row = 0;
    row <= 3;
    row += 1
  ) {
    for (
      let column = 0;
      column <= 3;
      column += 1
    ) {
      masks.push(
        maskFromCells([
          [row, column],
          [row, column + 1],
          [row + 1, column],
          [row + 1, column + 1],
        ])
      );
    }
  }

  return masks;
};


// ============================================================
// PATTERN LIBRARY
// ============================================================

export const bingoPatterns = [

  // ----------------------------------------------------------
  // BASIC
  // ----------------------------------------------------------

  {
    id: 'horizontal',
    name: 'Horizontal Line',
    shortName: 'Horizontal',
    category: 'Basic Lines',
    type: 'anyOf',

    masks: [
      horizontalRow(0),
      horizontalRow(1),
      horizontalRow(2),
      horizontalRow(3),
      horizontalRow(4),
    ],
  },

  {
    id: 'vertical',
    name: 'Vertical Line',
    shortName: 'Vertical',
    category: 'Basic Lines',
    type: 'anyOf',

    masks: [
      verticalColumn(0),
      verticalColumn(1),
      verticalColumn(2),
      verticalColumn(3),
      verticalColumn(4),
    ],
  },

  {
    id: 'diagonal',
    name: 'Diagonal Line',
    shortName: 'Diagonal',
    category: 'Basic Lines',
    type: 'anyOf',

    masks: [
      MAIN_DIAGONAL,
      REVERSE_DIAGONAL,
    ],
  },

  {
    id: 'anyLine',
    name: 'Any Straight Line',
    shortName: 'Any Line',
    category: 'Basic Lines',
    type: 'anyOf',

    masks: [
      horizontalRow(0),
      horizontalRow(1),
      horizontalRow(2),
      horizontalRow(3),
      horizontalRow(4),

      verticalColumn(0),
      verticalColumn(1),
      verticalColumn(2),
      verticalColumn(3),
      verticalColumn(4),

      MAIN_DIAGONAL,
      REVERSE_DIAGONAL,
    ],
  },


  // ----------------------------------------------------------
  // CORNERS / EDGES
  // ----------------------------------------------------------

  {
    id: 'corners',
    name: 'Four Corners',
    shortName: 'Four Corners',
    category: 'Corners & Edges',
    type: 'fixed',
    masks: [
      FOUR_CORNERS,
    ],
  },

  {
    id: 'topRow',
    name: 'Top Row',
    shortName: 'Top Row',
    category: 'Corners & Edges',
    type: 'fixed',
    masks: [
      horizontalRow(0),
    ],
  },

  {
    id: 'bottomRow',
    name: 'Bottom Row',
    shortName: 'Bottom Row',
    category: 'Corners & Edges',
    type: 'fixed',
    masks: [
      horizontalRow(4),
    ],
  },

  {
    id: 'leftColumn',
    name: 'Left Column',
    shortName: 'Left Column',
    category: 'Corners & Edges',
    type: 'fixed',
    masks: [
      verticalColumn(0),
    ],
  },

  {
    id: 'rightColumn',
    name: 'Right Column',
    shortName: 'Right Column',
    category: 'Corners & Edges',
    type: 'fixed',
    masks: [
      verticalColumn(4),
    ],
  },


  // ----------------------------------------------------------
  // LETTERS / SHAPES
  // ----------------------------------------------------------

  {
    id: 'x',
    name: 'X',
    shortName: 'X',
    category: 'Shapes',
    type: 'fixed',
    masks: [
      X_PATTERN,
    ],
  },

  {
    id: 'plus',
    name: 'Plus Sign',
    shortName: 'Plus',
    category: 'Shapes',
    type: 'fixed',
    masks: [
      PLUS_PATTERN,
    ],
  },

  {
    id: 't',
    name: 'Letter T',
    shortName: 'T',
    category: 'Letters',
    type: 'anyOf',
    masks:
      createRotations(
        T_PATTERN
      ),
  },

  {
    id: 'l',
    name: 'Letter L',
    shortName: 'L',
    category: 'Letters',
    type: 'anyOf',
    masks:
      createRotations(
        L_PATTERN
      ),
  },

  {
    id: 'h',
    name: 'Letter H',
    shortName: 'H',
    category: 'Letters',
    type: 'fixed',
    masks: [
      H_PATTERN,
    ],
  },

  {
    id: 'u',
    name: 'Letter U',
    shortName: 'U',
    category: 'Letters',
    type: 'anyOf',
    masks:
      createRotations(
        U_PATTERN
      ),
  },

  {
    id: 'v',
    name: 'Letter V',
    shortName: 'V',
    category: 'Letters',
    type: 'anyOf',
    masks:
      createRotations(
        V_PATTERN
      ),
  },

  {
    id: 'z',
    name: 'Letter Z',
    shortName: 'Z',
    category: 'Letters',
    type: 'anyOf',

    masks: [
      Z_PATTERN,
      mirrorMaskHorizontal(
        Z_PATTERN
      ),
    ],
  },

  {
    id: 'n',
    name: 'Letter N',
    shortName: 'N',
    category: 'Letters',
    type: 'anyOf',

    masks: [
      N_PATTERN,
      mirrorMaskHorizontal(
        N_PATTERN
      ),
    ],
  },

  {
    id: 'e',
    name: 'Letter E',
    shortName: 'E',
    category: 'Letters',
    type: 'anyOf',
    masks:
      createRotations(
        E_PATTERN
      ),
  },

  {
    id: 'c',
    name: 'Letter C',
    shortName: 'C',
    category: 'Letters',
    type: 'anyOf',
    masks:
      createRotations(
        C_PATTERN
      ),
  },


  // ----------------------------------------------------------
  // DIAMONDS
  // ----------------------------------------------------------

  {
    id: 'smallDiamond',
    name: 'Small Diamond',
    shortName: 'Small Diamond',
    category: 'Diamonds',
    type: 'fixed',
    masks: [
      SMALL_DIAMOND,
    ],
  },

  {
    id: 'largeDiamond',
    name: 'Large Diamond',
    shortName: 'Large Diamond',
    category: 'Diamonds',
    type: 'fixed',
    masks: [
      LARGE_DIAMOND,
    ],
  },


  // ----------------------------------------------------------
  // FRAMES / SQUARES
  // ----------------------------------------------------------

  {
    id: 'outsideFrame',
    name: 'Outside Frame',
    shortName: 'Outside Frame',
    category: 'Frames & Squares',
    type: 'fixed',
    masks: [
      OUTSIDE_FRAME,
    ],
  },

  {
    id: 'insideFrame',
    name: 'Inside Frame',
    shortName: 'Inside Frame',
    category: 'Frames & Squares',
    type: 'fixed',
    masks: [
      INSIDE_FRAME,
    ],
  },

  {
    id: 'smallSquare',
    name: 'Small Square',
    shortName: 'Small Square',
    category: 'Frames & Squares',
    type: 'fixed',
    masks: [
      SMALL_SQUARE,
    ],
  },


  // ----------------------------------------------------------
  // SPECIALTY
  // ----------------------------------------------------------

  {
    id: 'postageStamp',
    name: 'Postage Stamp',
    shortName: 'Stamp',
    category: 'Specialty',
    type: 'anyOf',

    masks: [
      POSTAGE_STAMP_TOP_LEFT,
      POSTAGE_STAMP_TOP_RIGHT,
      POSTAGE_STAMP_BOTTOM_LEFT,
      POSTAGE_STAMP_BOTTOM_RIGHT,
    ],
  },

  {
    id: 'blockOfFour',
    name: 'Block of Four',
    shortName: 'Block of 4',
    category: 'Specialty',
    type: 'anyOf',
    masks:
      createBlockOfFourMasks(),
  },

  {
    id: 'sixPack',
    name: 'Six Pack',
    shortName: 'Six Pack',
    category: 'Specialty',
    type: 'anyOf',
    masks:
      createSixPackMasks(),
  },

  {
    id: 'ninePack',
    name: 'Nine Pack',
    shortName: 'Nine Pack',
    category: 'Specialty',
    type: 'anyOf',
    masks:
      createNinePackMasks(),
  },

  {
    id: 'bowTie',
    name: 'Bow Tie',
    shortName: 'Bow Tie',
    category: 'Specialty',
    type: 'fixed',
    masks: [
      BOW_TIE,
    ],
  },

  {
    id: 'hourglass',
    name: 'Hourglass',
    shortName: 'Hourglass',
    category: 'Specialty',
    type: 'fixed',
    masks: [
      HOURGLASS,
    ],
  },

  {
    id: 'pyramid',
    name: 'Pyramid',
    shortName: 'Pyramid',
    category: 'Specialty',
    type: 'anyOf',
    masks:
      createRotations(
        PYRAMID
      ),
  },

  {
    id: 'christmasTree',
    name: 'Christmas Tree',
    shortName: 'Tree',
    category: 'Seasonal',
    type: 'fixed',
    masks: [
      CHRISTMAS_TREE,
    ],
  },


  // ----------------------------------------------------------
  // ARROWS / KITES
  // ----------------------------------------------------------

  {
    id: 'arrow',
    name: 'Arrow',
    shortName: 'Arrow',
    category: 'Arrows & Kites',
    type: 'anyOf',
    masks:
      createRotations(
        ARROW_UP
      ),
  },

  {
    id: 'kite',
    name: 'Kite',
    shortName: 'Kite',
    category: 'Arrows & Kites',
    type: 'anyOf',
    masks:
      createRotations(
        KITE
      ),
  },


  // ----------------------------------------------------------
  // COVERALL
  // ----------------------------------------------------------

  {
    id: 'fullCard',
    name: 'Full Card / Coverall',
    shortName: 'Coverall',
    category: 'Coverall',
    type: 'fixed',
    masks: [
      FULL_CARD,
    ],
  },


  // ----------------------------------------------------------
  // CUSTOM
  // ----------------------------------------------------------

  {
    id: 'custom',
    name: 'Custom Pattern',
    shortName: 'Custom',
    category: 'Custom',
    type: 'custom',
    masks: [],
  },
];


// ============================================================
// LOOKUP HELPERS
// ============================================================

export const getBingoPattern = (
  patternId
) =>
  bingoPatterns.find(
    (pattern) =>
      pattern.id === patternId
  ) || null;


export const getPatternLabel = (
  patternId
) => {
  const pattern =
    getBingoPattern(patternId);

  return (
    pattern?.name ||
    patternId ||
    'Unknown Pattern'
  );
};


export const getPatternsByCategory = () =>
  bingoPatterns.reduce(
    (categories, pattern) => {
      if (
        !categories[
          pattern.category
        ]
      ) {
        categories[
          pattern.category
        ] = [];
      }

      categories[
        pattern.category
      ].push(pattern);

      return categories;
    },
    {}
  );


export const bingoPatternCategories = [
  'Basic Lines',
  'Corners & Edges',
  'Letters',
  'Shapes',
  'Diamonds',
  'Frames & Squares',
  'Arrows & Kites',
  'Specialty',
  'Seasonal',
  'Coverall',
  'Custom',
];


// ============================================================
// PATTERN MATCHING HELPER
// ============================================================

/*
  This isn't required by BingoMachine yet,
  but it gives us a foundation for scanner
  verification later.

  markedCells should be a 5x5 boolean array.

  A pattern succeeds when every true cell in
  one valid mask is also true in markedCells.

  Extra daubed cells are allowed.
*/

export const doesMaskMatch = (
  markedCells,
  mask
) => {
  if (
    !Array.isArray(markedCells) ||
    !Array.isArray(mask)
  ) {
    return false;
  }

  for (
    let row = 0;
    row < 5;
    row += 1
  ) {
    for (
      let column = 0;
      column < 5;
      column += 1
    ) {
      if (
        mask[row]?.[column] &&
        !markedCells[row]?.[column]
      ) {
        return false;
      }
    }
  }

  return true;
};


export const doesPatternMatch = (
  patternId,
  markedCells,
  customMask = null
) => {
  if (
    patternId === 'custom' &&
    customMask
  ) {
    return doesMaskMatch(
      markedCells,
      customMask
    );
  }

  const pattern =
    getBingoPattern(patternId);

  if (!pattern) {
    return false;
  }

  return pattern.masks.some(
    (mask) =>
      doesMaskMatch(
        markedCells,
        mask
      )
  );
};


export default bingoPatterns;